import React, { useMemo, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Calendar as CalendarIcon, Home, Settings, Shield } from "lucide-react";

import { AppStateProvider, useAppState } from "./context/AppStateContext";
import ErrorBoundary from "./components/ErrorBoundary";
import TabButton from "./components/ui/TabButton";

import HomePage from "./pages/HomePage";
import TrackPage from "./pages/TrackPage";
import RemindersPage from "./pages/RemindersPage";
import DailyDeedPage from "./pages/DailyDeedPage";
import SettingsPage from "./pages/SettingsPage";
import TemptedPage from "./pages/TemptedPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import WisdomPage from "./pages/WisdomPage";
import PulsePage from "./pages/PulsePage";
//import Navigation from "./components/Navigation";

function Shell() {
  const [tab, setTab] = useState("home");
  const [pinEntry, setPinEntry] = useState("");
  const [pinError, setPinError] = useState("");
  const { today, privacyLocked, state, actions } = useAppState();

  const headerTitle = useMemo(() => {
    const titles = {
      home: "Home",
      track: "Calendar",
      reminders: "Reminders",
      wisdom: "Wisdom",
      pulse: "Community Pulse",
      deed: "Daily Deed",
      tempted: "Help",
      analytics: "Analytics",
      settings: "Settings",
    };
    return titles[tab] || "Good Deeds";
  }, [tab]);

  // Memoize tab change handler
  const handleTabChange = useCallback((newTab) => {
    setTab(newTab);
  }, []);

  // Handle PIN entry for unlock
  const handlePinEntry = useCallback((digit) => {
    if (pinEntry.length >= 4) return;
    setPinEntry(prev => prev + digit);
    setPinError("");
  }, [pinEntry]);

  // Handle PIN submission
  const handleUnlockWithPin = useCallback(() => {
    const storedPin = state.settings?.appPin;
    if (pinEntry === storedPin) {
      actions.unlockAppPrivacy?.();
      setPinEntry("");
      setPinError("");
    } else {
      setPinError("Incorrect PIN");
      setPinEntry("");
    }
  }, [pinEntry, state.settings?.appPin, actions]);

  // Handle PIN backspace
  const handlePinBackspace = useCallback(() => {
    setPinEntry(prev => prev.slice(0, -1));
  }, []);

  // Lock app when it goes to background (visibility change)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && state.settings?.privacyLock) {
        // App went to background and privacy lock is enabled
        actions.lockAppPrivacy?.();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [state.settings?.privacyLock, actions]);

  // Memoize page components to prevent remounting on tab change
  const pageContent = useMemo(() => {
    const pages = {
      home: <HomePage goTo={setTab} />,
      track: <TrackPage goTo={setTab} />,
      tempted: <TemptedPage goHome={() => setTab("home")} />,
      analytics: <AnalyticsPage goTo={setTab} />,
      reminders: <RemindersPage goTo={setTab} />,
      wisdom: <WisdomPage />,
      pulse: <PulsePage />,
      deed: <DailyDeedPage />,
      settings: <SettingsPage />,
    };
    return pages[tab] || pages.home;
  }, [tab]);

  // Privacy lock screen with PIN entry
  if (privacyLocked) {
    return (
      <div className="bg-midnight text-zinc-50 flex flex-col h-screen overflow-hidden">
        {/* White status bar */}
        <div className="fixed top-0 left-0 right-0 z-100 bg-white w-full"
          style={{ 
            position: "fixed",
            top: 0,
            width: "100%",
            zIndex: 100,
            backgroundColor: "white",
            paddingTop: "env(safe-area-inset-top, 20px)",
            boxSizing: "border-box",
            height: "env(safe-area-inset-top, 20px)"
          }}
        />

        {/* Dark header below status bar */}
        <div className="fixed top-0 left-0 right-0 z-100 bg-midnight border-b border-zinc-800 w-full"
          style={{ 
            position: "fixed",
            top: "env(safe-area-inset-top, 20px)",
            width: "100%",
            zIndex: 100,
            backgroundColor: "#09090B",
            boxSizing: "border-box"
          }}
        >
          <div className="max-w-md mx-auto px-4 py-2 flex items-center justify-between">
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-widest font-semibold">Good Deeds</div>
              <div className="font-semibold text-white">Privacy Lock</div>
            </div>
            <div className="text-xs text-zinc-500">{today}</div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-4 bg-midnight" style={{ height: "100%", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch", paddingTop: "calc(55px + env(safe-area-inset-top, 20px))", paddingBottom: "100px" }}>
          {/* Invisible spacer to account for fixed header + safe-area-inset */}
          <div className="header-spacer" style={{ position: "absolute", top: 0, left: 0, right: 0 }} />
          
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl bg-zinc-900/70 shadow-sm ring-1 ring-white/10 p-6 max-w-sm w-full space-y-6"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl bg-zinc-800 flex items-center justify-center flex-shrink-0">
                <Shield className="h-5 w-5 text-zinc-300" />
              </div>
              <div>
                <div className="font-semibold">App Locked</div>
                <div className="text-sm text-zinc-400">
                  Enter your 4-digit PIN to unlock
                </div>
              </div>
            </div>

            {/* PIN Display */}
            <div className="flex gap-2 justify-center">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-12 h-12 rounded-xl bg-zinc-800 border border-zinc-700 flex items-center justify-center"
                >
                  {pinEntry[i] ? (
                    <div className="w-3 h-3 rounded-full bg-[#10B981]"></div>
                  ) : null}
                </div>
              ))}
            </div>

            {/* Error Message */}
            {pinError && (
              <div className="text-center text-red-400 text-sm font-medium">
                {pinError}
              </div>
            )}

            {/* Numeric Keypad */}
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                <button
                  key={digit}
                  onClick={() => handlePinEntry(digit.toString())}
                  disabled={pinEntry.length >= 4}
                  className="aspect-square rounded-xl bg-zinc-800 border border-zinc-700 text-lg font-semibold hover:bg-zinc-700 active:scale-95 disabled:opacity-50 transition-all"
                >
                  {digit}
                </button>
              ))}
              <button
                onClick={() => handlePinEntry("0")}
                disabled={pinEntry.length >= 4}
                className="col-span-1 aspect-square rounded-xl bg-zinc-800 border border-zinc-700 text-lg font-semibold hover:bg-zinc-700 active:scale-95 disabled:opacity-50 transition-all"
              >
                0
              </button>
              <button
                onClick={handlePinBackspace}
                className="col-span-2 rounded-xl bg-zinc-800 border border-zinc-700 text-sm font-semibold hover:bg-zinc-700 active:scale-95 transition-all"
              >
                Delete
              </button>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleUnlockWithPin}
              disabled={pinEntry.length !== 4}
              className="w-full rounded-xl bg-[#10B981] text-zinc-900 py-3 font-semibold disabled:opacity-50 hover:bg-emerald-600 transition active:scale-95 min-h-[44px]"
            >
              Unlock
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-midnight flex flex-col h-screen overflow-hidden">
      {/* White status bar */}
      <div className="fixed top-0 left-0 right-0 z-100 bg-white w-full"
        style={{ 
          position: "fixed",
          top: 0,
          width: "100%",
          zIndex: 100,
          backgroundColor: "white",
          paddingTop: "env(safe-area-inset-top, 20px)",
          boxSizing: "border-box",
          height: "env(safe-area-inset-top, 20px)"
        }}
      />

      {/* Dark header below status bar */}
      <div className="fixed top-0 left-0 right-0 z-100 bg-midnight border-b border-zinc-800 w-full"
        style={{ 
          position: "fixed",
          top: "env(safe-area-inset-top, 20px)",
          width: "100%",
          zIndex: 100,
          backgroundColor: "#09090B",
          boxSizing: "border-box"
        }}
      >
        <div className="max-w-md sm:max-w-2xl mx-auto px-4 py-2 flex items-center justify-between">
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest font-semibold">Good Deeds</div>
            <div className="font-semibold text-white">{headerTitle}</div>
          </div>
          <div className="text-xs text-zinc-500">{today}</div>
        </div>
      </div>

      {/* Main content - scrollable area */}
      <div className="flex-1 overflow-y-auto bg-midnight" style={{ height: "100%", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch", paddingTop: "calc(55px + env(safe-area-inset-top, 20px))", paddingBottom: "100px" }}>
        
        <div className="max-w-md sm:max-w-2xl mx-auto px-4 pt-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
            >
              {pageContent}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Fixed bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-midnight border-t border-zinc-800"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="max-w-md sm:max-w-2xl mx-auto">
          <div className="rounded-2xl bg-charcoal border border-zinc-800 shadow-lg ring-1 ring-mint-accent/10 p-2 m-2">
            <div className="grid grid-cols-5 gap-1" role="tablist" aria-label="Main navigation">
              <TabButton active={tab === "home"} onClick={() => handleTabChange("home")} icon={Home} label="Home" />
              <TabButton active={tab === "track"} onClick={() => handleTabChange("track")} icon={CalendarIcon} label="Track" />
              <TabButton active={tab === "wisdom"} onClick={() => handleTabChange("wisdom")} icon={Bell} label="Wisdom" />
              <TabButton active={tab === "pulse"} onClick={() => handleTabChange("pulse")} icon={Home} label="Pulse" />
              <TabButton active={tab === "settings"} onClick={() => handleTabChange("settings")} icon={Settings} label="Settings" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppStateProvider>
        <div className="App">
          <Shell />
        </div>
      </AppStateProvider>
    </ErrorBoundary>
  );
}
